/**
 * Entry application component used to compose providers and render Routes.
 * */

import CssBaseline from '@mui/material/CssBaseline';
import makeStyles from '@mui/styles/makeStyles';
import { EventEmitter } from 'events';
import React from 'react';
import TagManager from 'react-gtm-module';
import { BrowserRouter } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValue,
  useRecoilValueLoadable,
} from 'recoil';
import {
  mixpanelRegisterUser,
  mixpanelReset,
  mixpanelSetUser,
} from 'src/common/userTracking';
import { CreateSubscription } from '../api/apollo-appsync/subscriptions/CreateSubscription';
import { themeModeAtom, themeModeEnum } from '../recoil/atoms/themeModeAtom';
import { user } from '../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
} from '../recoil/selectors/makePHPRequest';
import { I18nProvider } from '../_metronic/i18n';
import { SnackbarProviderWithStyles } from './components/notifications/SnackbarProviderWithStyles';
import { useMixpanelManager } from './hooks/useMixpanelManager';
import { HubspotVisitorIdentificationAPI } from './modules/Hubspot/VisitorIdentificationAPI';
import { DashboardTour } from './pages/dashboardTour/DashboardTour';
import { DashboardTourProvider } from './pages/dashboardTour/DashboardTourContext';
import Routes from './Routes';
import { MaterialThemeProvider } from './themes/MaterialThemeProvider';
import { DynamicSplitProvider } from './components/split/DynamicSplitProvider';
import { useDynamicSplitConfig } from './hooks/useDynamicSplitConfig';

// import {
//   selectAccessTokenJson,
//   selectUserSettings,
// } from './store/userSlice';
// import { useSelector } from 'react-redux';
// import { loginPut, selectLoginSettings } from './store/loginSlice';

const tagManagerConfig = {
  gtmId: 'GTM-ML35XS8',
};
TagManager.initialize(tagManagerConfig);

// increase emmitter from 10 to 12 due to increased number of columns on DataTable
EventEmitter.prototype._maxListeners = 20;

const useStyles = makeStyles((theme) => ({
  kt_content: ({ themeMode }) => ({
    backgroundColor:
      themeMode === themeModeEnum.DARK_MODE ? '#011318' : 'transparent',
  }),
}));

export default function App({ basename }) {
  const [userAtom, _setUserAtom] = useRecoilStateLoadable(user);
  const themeMode = useRecoilValue(themeModeAtom);
  const classes = useStyles({ themeMode });
  const billingStatus = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_BILLING_STATUS,
      payloadJsonString: JSON.stringify({
        businessid: userAtom?.contents?.businessid,
      }),
    })
  );

  // Use the custom hook for Mixpanel management
  const { checkAndManageMixpanel } = useMixpanelManager(
    userAtom?.contents?.userSettings
  );

  // Call checkAndManageMixpanel on initial mount
  React.useEffect(() => {
    checkAndManageMixpanel();
  }, [checkAndManageMixpanel]);

  const userId = userAtom?.contents?.userSettings?.userid;
  const businessId = userAtom?.contents?.businessid;

  // Generate dynamic Split.io configuration using custom hook
  const splitConfig = useDynamicSplitConfig({
    businessId,
    userId,
    userSettings: userAtom?.contents?.userSettings,
  });

  React.useEffect(() => {
    if (!billingStatus || !userAtom?.contents) return;
    // Add Global/Super properties to Mixpanel events
    mixpanelRegisterUser(userAtom?.contents?.userSettings);
    if (userAtom.contents?.userSettings?.userid) {
      mixpanelSetUser(userAtom?.contents?.userSettings);
    } else {
      mixpanelReset();
    }
  }, [userAtom.contents, billingStatus, userAtom.contents?.email]);
  // const userSettings = useSelector(selectUserSettings);
  // const loginSettings = useSelector(selectLoginSettings);
  // const accessTokenJson = useSelector(selectAccessTokenJson);

  return (
    /* Override `basename` (e.g: `homepage` in `package.json`) */
    <DynamicSplitProvider config={splitConfig}>
      <HubspotVisitorIdentificationAPI user={userAtom?.contents?.user} />
      <SnackbarProviderWithStyles>
        <BrowserRouter basename={basename}>
          <CreateSubscription
            // loginPut={loginPut}
            // loginSettings={loginSettings}
            setUserAtom={_setUserAtom}
            userSettings={userAtom.contents?.userSettings ?? {}}
            // accessTokenJson={accessTokenJson}
          />
          {/* This library only returns the location that has been active before the recent location change in the current window lifetime. */}
          <MaterialThemeProvider themeMode={themeMode}>
            {/* Provide `react-intl` context synchronized with Redux state.  */}
            <CssBaseline />
            <I18nProvider>
              <DashboardTourProvider>
                <DashboardTour />
                {/* Render routes with provided `Layout`. */}
                <Routes
                  classes={{
                    root: themeMode,
                    ...classes,
                  }}
                />
              </DashboardTourProvider>
            </I18nProvider>
          </MaterialThemeProvider>
        </BrowserRouter>
              </SnackbarProviderWithStyles>
    </DynamicSplitProvider>
    /* Provide Redux store */
  );
}
